import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  doc,
  Timestamp,
  deleteField,
} from 'firebase/firestore';
import { Plus, Settings as SettingsIcon } from 'lucide-react';
import { db } from '../utils/firebase';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import AppHeader from '../components/ui/AppHeader';
import CpaHeader from '../components/finances/CpaHeader';
import FinancesSidebar, { FinancesSection } from '../components/finances/FinancesSidebar';
import OverheadTable from '../components/finances/OverheadTable';
import OverheadEditModal from '../components/finances/OverheadEditModal';
import PayrollTable from '../components/finances/PayrollTable';
import PayrollEditModal from '../components/finances/PayrollEditModal';
import PayrollColaModal from '../components/finances/PayrollColaModal';
import PayrollSettingsModal from '../components/finances/PayrollSettingsModal';
import ForecastView from '../components/finances/ForecastView';
import ProjectPLSection from '../components/finances/ProjectPLSection';
import ProgressBillingSection, {
  ProgressBillingAsOfPicker,
  ProgressBillingMethodInfo,
  progressBillingTodayInput,
} from '../components/finances/ProgressBillingSection';
import { canAccessProjectPL, canReviewDesignerInvoices } from '../utils/projectPLAccess';
import {
  OverheadExpense,
  PayrollDepartment,
  PayrollPosition,
  PayrollSettings,
  DEFAULT_PAYROLL_SETTINGS,
  PAYROLL_STATUSES,
  PAYROLL_STATUS_LABELS,
  clampOverheadRecurringDay,
  clampOverheadRenewalMonth,
} from '../types/finances';
import { activeHeadcountForMonth, currentMonthlyBurn, payrollMonthlyByStatus } from '../utils/financesForecast';
import { budgetedMonthlyByGroup, emptyOpexBudgets, saveOpexGroupBudget, subscribeOpexBudgets, totalBudgetedOpexMonthly, type OpexBudgets } from '../utils/opexBudgets';
import type { OpexBudgetInterval } from '../utils/opexBudgetMath';
import { isPurchaseOverheadGroup } from '../utils/purchasingSpendClass';
import type { PurchaseOverheadGroup } from '../types/purchasing';
import { applyDueScheduledRaises, newScheduledRaiseId } from '../utils/payrollCompSchedule';
import { parsePositionCompFields, positionCompFirestorePatch, serializeCompPeriods, serializeScheduledRaises } from '../utils/payrollFirestore';
import { firestoreSnapshotListenError } from '../utils/firestoreListenDebug';
import { useContractorPayablesAccess } from '../hooks/useContractorPayablesAccess';
import { useCpaPortalPreview } from '../hooks/useCpaPortalPreview';
import { canCommentOnFinances } from '../utils/contractorPayablesAccess';
import { subscribeAllContractorInvoices } from '../utils/contractorTimesheetService';
import { subscribePendingDesignerInvoices } from '../utils/designerInvoiceService';
import ContractorPayablesPage from './ContractorPayablesPage';
import RegulatorySection from '../components/finances/RegulatorySection';
import '../components/pm/pm-shared.css';
import './FinancesHub.css';

const SECTION_BY_PATH: Record<string, FinancesSection> = {
  '/finances': 'forecast',
  '/finances/forecast': 'forecast',
  '/finances/overview': 'forecast',
  '/finances/overhead': 'overhead',
  '/finances/payroll': 'payroll',
  '/finances/project-pl': 'project-pl',
  '/finances/progress-billing': 'progress-billing',
  '/finances/contractor-payables': 'contractor-payables',
  '/finances/regulatory': 'regulatory',
};

const PATH_BY_SECTION: Record<FinancesSection, string> = {
  forecast: '/finances',
  overhead: '/finances/overhead',
  payroll: '/finances/payroll',
  'project-pl': '/finances/project-pl',
  'progress-billing': '/finances/progress-billing',
  'contractor-payables': '/finances/contractor-payables',
  regulatory: '/finances/regulatory',
};

function pickSection(pathname: string): FinancesSection {
  const p = pathname.replace(/\/$/, '');
  return SECTION_BY_PATH[p] ?? 'forecast';
}

const SECTION_TITLES: Record<FinancesSection, string> = {
  forecast: 'Forecast',
  overhead: 'OpEx',
  payroll: 'Payroll & positions',
  'project-pl': 'Project P&L',
  'progress-billing': 'Progress Billing',
  'contractor-payables': 'Contractor payables',
  regulatory: 'Regulatory',
};

const LEGACY_FINANCES_PATHS = new Set(['/finances/overview', '/finances/forecast']);

const PAYROLL_SETTINGS_DOC = 'payrollSettings';

const formatFinStatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

const FinancesHub: React.FC = () => {
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    canAccess: canPayablesRaw,
    canFullFinances: showFullFinancesRaw,
    canWriteFinances: canWriteFinancesRaw,
    designations,
  } = useContractorPayablesAccess();
  const { isCpaShell, isPreview, enterPreview, exitPreview } = useCpaPortalPreview();
  const canPayables = isCpaShell || canPayablesRaw;
  const showFullFinances = isCpaShell || showFullFinancesRaw;
  const canWriteFinances = isCpaShell ? false : canWriteFinancesRaw;
  const me = crmUsers.find((u) => u.uid === user?.id);
  const briefingProfile = user?.executiveBriefing?.profile ?? me?.executiveBriefing?.profile ?? null;
  const canAccessPLRaw = useMemo(
    () =>
      canAccessProjectPL({
        authRole: user?.role,
        designations: me?.designations ?? user?.designations,
      }),
    [me?.designations, user?.designations, user?.role],
  );
  const canReviewDesignerInvoiceQueue = useMemo(
    () =>
      canReviewDesignerInvoices({
        authRole: user?.role,
        designations: me?.designations ?? user?.designations ?? designations,
        briefingProfile,
      }),
    [briefingProfile, designations, me?.designations, user?.designations, user?.role],
  );
  const canAccessPL = isCpaShell || canAccessPLRaw;
  const canComment = isCpaShell || canCommentOnFinances({
    authRole: user?.role,
    designations,
  });
  const HubHeader = isCpaShell ? CpaHeader : AppHeader;
  const section = useMemo(() => pickSection(location.pathname), [location.pathname]);
  const [pendingDesignerInvoiceCount, setPendingDesignerInvoiceCount] = useState(0);
  // Bumped by the toolbar button; RegulatorySection opens its modal when it changes.
  const [newBondRequestNonce, setNewBondRequestNonce] = useState(0);

  useEffect(() => {
    if (isCpaShell || !canReviewDesignerInvoiceQueue) {
      setPendingDesignerInvoiceCount(0);
      return undefined;
    }
    return subscribePendingDesignerInvoices((list) => {
      setPendingDesignerInvoiceCount(list.length);
    });
  }, [canReviewDesignerInvoiceQueue, isCpaShell]);

  const handleCreateJobPosting = useCallback((position: PayrollPosition) => {
    const params = new URLSearchParams({
      new: '1',
      payrollPositionId: position.id,
      title: position.title,
      department: position.department,
    });
    navigate(`/people/hiring?${params.toString()}`);
  }, [navigate]);

  useEffect(() => {
    if (location.pathname.replace(/\/$/, '') === '/finances/designer-invoices') {
      navigate('/finances/project-pl?review=designer-invoices', { replace: true });
      return;
    }
    const allowed = new Set<FinancesSection>();
    if (showFullFinances) {
      allowed.add('forecast');
      allowed.add('overhead');
      allowed.add('payroll');
      allowed.add('progress-billing');
      allowed.add('regulatory');
    }
    if (canAccessPL) allowed.add('project-pl');
    if (canPayables) allowed.add('contractor-payables');
    if (allowed.has(section)) return;
    if (allowed.has('forecast')) {
      navigate('/finances', { replace: true });
      return;
    }
    if (allowed.has('project-pl')) {
      navigate('/finances/project-pl', { replace: true });
      return;
    }
    if (allowed.has('contractor-payables')) {
      navigate('/finances/contractor-payables', { replace: true });
    }
  }, [canAccessPL, canPayables, location.pathname, navigate, section, showFullFinances]);

  useEffect(() => {
    const p = location.pathname.replace(/\/$/, '');
    if (LEGACY_FINANCES_PATHS.has(p)) {
      navigate('/finances', { replace: true });
    }
  }, [location.pathname, navigate]);

  // B returns to Billing & Payables (unmodified — Cmd/Ctrl+B stays browser).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.code !== 'KeyB') return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      navigate('/billing-payables');
    };
    if (isCpaShell) return;
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isCpaShell, navigate]);

  const [overhead, setOverhead] = useState<OverheadExpense[]>([]);
  const [positions, setPositions] = useState<PayrollPosition[]>([]);
  const [settings, setSettings] = useState<PayrollSettings>(DEFAULT_PAYROLL_SETTINGS);
  const [loaded, setLoaded] = useState(false);
  const [progressAsOfInput, setProgressAsOfInput] = useState(progressBillingTodayInput);
  const [editingOverhead, setEditingOverhead] = useState<Partial<OverheadExpense> | null>(null);
  const [editingPosition, setEditingPosition] = useState<Partial<PayrollPosition> | null>(null);
  const [showColaModal, setShowColaModal] = useState(false);
  const [editingSettings, setEditingSettings] = useState(false);
  const applyingRaisesRef = useRef(false);
  const [overheadLoaded, setOverheadLoaded] = useState(false);
  const [opexBudgets, setOpexBudgets] = useState<OpexBudgets>(() => emptyOpexBudgets());
  const [positionsLoaded, setPositionsLoaded] = useState(false);
  const [pendingContractorInvoices, setPendingContractorInvoices] = useState(0);

  useEffect(() => {
    if (!showFullFinances) {
      setLoaded(true);
      return;
    }
    if (overheadLoaded && positionsLoaded) setLoaded(true);
  }, [overheadLoaded, positionsLoaded, showFullFinances]);

  useEffect(() => {
    if (!canPayables) return;
    const unsub = subscribeAllContractorInvoices((rows) => {
      setPendingContractorInvoices(rows.filter((r) => r.status === 'pending_payment').length);
    }, (err) =>
      firestoreSnapshotListenError('FinancesHub', 'contractorInvoices', err, {
        userId: user?.id,
        role: user?.role,
        email: user?.email,
      }),
    );
    return unsub;
  }, [canPayables, user?.id, user?.role, user?.email]);

  // ── Overhead listener ──
  useEffect(() => {
    if (!showFullFinances) return;
    const q = query(collection(db, 'overheadExpenses'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      snap => {
        const rows: OverheadExpense[] = snap.docs.map(d => {
          const data: any = d.data();
          return {
            id: d.id,
            name: data.name || '',
            category: data.category || 'other',
            amount: data.amount || 0,
            frequency: data.frequency || 'monthly',
            scalesWith: data.scalesWith === 'per_active_employee' ? 'per_active_employee' : 'fixed',
            startDate: data.startDate?.toDate?.() || new Date(),
            recurringDay: clampOverheadRecurringDay(data.recurringDay) ?? undefined,
            renewalMonth: clampOverheadRenewalMonth(data.renewalMonth) ?? undefined,
            endDate: data.endDate?.toDate?.() || null,
            vendor: data.vendor || undefined,
            notes: data.notes || undefined,
            isActive: data.isActive !== false,
            opexGroup: isPurchaseOverheadGroup(data.opexGroup) ? data.opexGroup : undefined,
            createdAt: data.createdAt?.toDate?.() || new Date(),
            updatedAt: data.updatedAt?.toDate?.() || new Date(),
            createdBy: data.createdBy || '',
          };
        });
        setOverhead(rows);
        setOverheadLoaded(true);
      },
      err => firestoreSnapshotListenError('FinancesHub', 'overheadExpenses', err, {
        userId: user?.id,
        role: user?.role,
        email: user?.email,
      }),
    );
    return unsub;
  }, [user?.id, user?.role, user?.email, showFullFinances]);

  useEffect(() => {
    if (!showFullFinances) return;
    return subscribeOpexBudgets(setOpexBudgets);
  }, [showFullFinances]);

  // ── Payroll positions listener ──
  useEffect(() => {
    if (!showFullFinances) return;
    const q = query(collection(db, 'payrollPositions'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      snap => {
        const rows: PayrollPosition[] = snap.docs.map(d => {
          const data: any = d.data();
          const { scheduledRaises, compPeriods } = parsePositionCompFields(data);
          // Legacy migration: map old flat burden % into employerTaxRateOverride so
          // loaded cost stays visually constant until the admin re-edits.
          const hasLegacyBurden =
            data.benefitsAndTaxRate !== undefined &&
            data.benefitsAndTaxRate !== null &&
            data.healthReimbursementMonthly === undefined;
          const employerTaxRateOverride = hasLegacyBurden
            ? Number(data.benefitsAndTaxRate)
            : (data.employerTaxRateOverride ?? null);
          const payType: 'salary' | 'hourly' = data.payType === 'hourly' ? 'hourly' : 'salary';
          return {
            id: d.id,
            title: data.title || '',
            department: data.department || 'other',
            status: data.status || 'filled',
            payType,
            annualSalary: data.annualSalary || 0,
            hourlyRate: typeof data.hourlyRate === 'number' ? data.hourlyRate : null,
            hoursPerWeek: typeof data.hoursPerWeek === 'number' ? data.hoursPerWeek : null,
            healthReimbursementMonthly: data.healthReimbursementMonthly ?? 0,
            ptoDaysPerYear: data.ptoDaysPerYear ?? 0,
            monthlyEmployeeExtras: data.monthlyEmployeeExtras ?? 0,
            monthlyEmployeeExtrasNote: data.monthlyEmployeeExtrasNote || undefined,
            sepIraRateOverride: data.sepIraRateOverride ?? null,
            employerTaxRateOverride,
            benefitsAndTaxRate: hasLegacyBurden ? Number(data.benefitsAndTaxRate) : null,
            startDate: data.startDate?.toDate?.() || new Date(),
            endDate: data.endDate?.toDate?.() || null,
            crmUserId: data.crmUserId ?? null,
            workState: data.workState ?? null,
            notes: data.notes || undefined,
            isActive: data.isActive !== false,
            scheduledRaises,
            compPeriods,
            createdAt: data.createdAt?.toDate?.() || new Date(),
            updatedAt: data.updatedAt?.toDate?.() || new Date(),
            createdBy: data.createdBy || '',
          };
        });
        setPositions(rows);
        setPositionsLoaded(true);

        if (!applyingRaisesRef.current) {
          const dueUpdates: PayrollPosition[] = [];
          for (const row of rows) {
            const updated = applyDueScheduledRaises(row, new Date());
            if (updated) dueUpdates.push(updated);
          }
          if (dueUpdates.length > 0) {
            applyingRaisesRef.current = true;
            Promise.all(
              dueUpdates.map(p =>
                updateDoc(doc(db, 'payrollPositions', p.id), {
                  ...positionCompFirestorePatch(p),
                  updatedAt: Timestamp.fromDate(new Date()),
                }),
              ),
            )
              .catch(err => console.error('[FinancesHub] Auto-apply raises failed:', err))
              .finally(() => {
                applyingRaisesRef.current = false;
              });
          }
        }
      },
      err => firestoreSnapshotListenError('FinancesHub', 'payrollPositions', err, {
        userId: user?.id,
        role: user?.role,
        email: user?.email,
      }),
    );
    return unsub;
  }, [user?.id, user?.role, user?.email, showFullFinances]);

  // ── Payroll settings listener ──
  useEffect(() => {
    if (!showFullFinances) return;
    const ref = doc(db, 'financeSettings', PAYROLL_SETTINGS_DOC);
    const unsub = onSnapshot(
      ref,
      snap => {
        if (!snap.exists()) {
          setSettings(DEFAULT_PAYROLL_SETTINGS);
          return;
        }
        const data: any = snap.data();
        setSettings({
          sepIraRate: typeof data.sepIraRate === 'number' ? data.sepIraRate : DEFAULT_PAYROLL_SETTINGS.sepIraRate,
          employerTaxRate: typeof data.employerTaxRate === 'number' ? data.employerTaxRate : DEFAULT_PAYROLL_SETTINGS.employerTaxRate,
          defaultPtoDaysPerYear: typeof data.defaultPtoDaysPerYear === 'number' ? data.defaultPtoDaysPerYear : DEFAULT_PAYROLL_SETTINGS.defaultPtoDaysPerYear,
          defaultHealthReimbursementMonthly: typeof data.defaultHealthReimbursementMonthly === 'number' ? data.defaultHealthReimbursementMonthly : 0,
          defaultMonthlyEmployeeExtras: typeof data.defaultMonthlyEmployeeExtras === 'number' ? data.defaultMonthlyEmployeeExtras : 0,
          defaultWorkState: typeof data.defaultWorkState === 'string' && data.defaultWorkState.trim()
            ? data.defaultWorkState.trim().toUpperCase()
            : DEFAULT_PAYROLL_SETTINGS.defaultWorkState,
          updatedAt: data.updatedAt?.toDate?.() || null,
          updatedBy: data.updatedBy || undefined,
        });
      },
      err => firestoreSnapshotListenError('FinancesHub', 'financeSettings/payrollSettings', err, {
        userId: user?.id,
        role: user?.role,
        email: user?.email,
      }),
    );
    return unsub;
  }, [user?.id, user?.role, user?.email]);

  // ── Mutations ──
  const upsertOverhead = useCallback(async (data: Partial<OverheadExpense>) => {
    if (!user) return;
    const now = Timestamp.fromDate(new Date());
    const payload: any = {
      name: data.name ?? '',
      category: data.category ?? 'other',
      amount: data.amount ?? 0,
      frequency: data.frequency ?? 'monthly',
      scalesWith: data.scalesWith === 'per_active_employee' ? 'per_active_employee' : 'fixed',
      startDate: data.startDate ? Timestamp.fromDate(new Date(data.startDate)) : null,
      recurringDay: clampOverheadRecurringDay(data.recurringDay),
      renewalMonth: data.frequency === 'annually'
        ? clampOverheadRenewalMonth(data.renewalMonth)
        : null,
      endDate: data.endDate ? Timestamp.fromDate(new Date(data.endDate)) : null,
      vendor: data.vendor ?? null,
      notes: data.notes ?? null,
      isActive: data.isActive !== false,
      opexGroup: isPurchaseOverheadGroup(data.opexGroup) ? data.opexGroup : null,
      updatedAt: now,
    };
    Object.keys(payload).forEach(k => {
      if (payload[k] === undefined) payload[k] = null;
    });

    if (editingOverhead?.id) {
      await updateDoc(doc(db, 'overheadExpenses', editingOverhead.id), payload);
    } else {
      await addDoc(collection(db, 'overheadExpenses'), {
        ...payload,
        createdAt: now,
        createdBy: user.id,
      });
    }
  }, [editingOverhead?.id, user]);

  const deleteOverhead = useCallback(async () => {
    if (!editingOverhead?.id) return;
    await deleteDoc(doc(db, 'overheadExpenses', editingOverhead.id));
  }, [editingOverhead?.id]);

  const upsertPosition = useCallback(async (data: Partial<PayrollPosition>) => {
    if (!user) return;
    const now = Timestamp.fromDate(new Date());
    let compPeriods = data.compPeriods;
    const scheduledRaises = data.scheduledRaises ?? [];
    if (!compPeriods?.length && data.startDate && (data.annualSalary ?? 0) > 0) {
      compPeriods = [
        {
          id: `period-${Date.now()}-initial`,
          effectiveDate: new Date(data.startDate),
          endDate: null,
          annualSalary: data.annualSalary ?? 0,
          hourlyRate: data.payType === 'hourly' ? (data.hourlyRate ?? null) : null,
          hoursPerWeek: data.payType === 'hourly' ? (data.hoursPerWeek ?? null) : null,
          label: 'Initial',
          source: 'hire' as const,
        },
      ];
    }
    const payload: any = {
      title: data.title ?? '',
      department: data.department ?? 'other',
      status: data.status ?? 'filled',
      payType: data.payType === 'hourly' ? 'hourly' : 'salary',
      annualSalary: data.annualSalary ?? 0,
      hourlyRate: data.payType === 'hourly' ? (data.hourlyRate ?? null) : null,
      hoursPerWeek: data.payType === 'hourly' ? (data.hoursPerWeek ?? null) : null,
      healthReimbursementMonthly: data.healthReimbursementMonthly ?? 0,
      ptoDaysPerYear: data.ptoDaysPerYear ?? settings.defaultPtoDaysPerYear,
      monthlyEmployeeExtras: data.monthlyEmployeeExtras ?? 0,
      monthlyEmployeeExtrasNote: data.monthlyEmployeeExtrasNote ?? null,
      sepIraRateOverride: data.sepIraRateOverride ?? null,
      employerTaxRateOverride: data.employerTaxRateOverride ?? null,
      startDate: data.startDate ? Timestamp.fromDate(new Date(data.startDate)) : null,
      endDate: data.endDate ? Timestamp.fromDate(new Date(data.endDate)) : null,
      crmUserId: data.crmUserId ?? null,
      workState: data.workState ?? null,
      notes: data.notes ?? null,
      isActive: data.isActive !== false,
      scheduledRaises: serializeScheduledRaises(scheduledRaises),
      compPeriods: serializeCompPeriods(compPeriods),
      updatedAt: now,
      benefitsAndTaxRate: deleteField(),
    };
    Object.keys(payload).forEach(k => {
      if (payload[k] === undefined) payload[k] = null;
    });

    if (editingPosition?.id) {
      await updateDoc(doc(db, 'payrollPositions', editingPosition.id), payload);
    } else {
      // Don't send a deleteField sentinel on create.
      const { benefitsAndTaxRate: _drop, ...createPayload } = payload;
      await addDoc(collection(db, 'payrollPositions'), {
        ...createPayload,
        createdAt: now,
        createdBy: user.id,
      });
    }
  }, [editingPosition?.id, user, settings.defaultPtoDaysPerYear]);

  const applyCola = useCallback(
    async (args: {
      effectiveDate: Date;
      percent: number;
      label: string;
      departments: PayrollDepartment[] | 'all';
    }) => {
      if (!user) return;
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (args.effectiveDate < today) {
        throw new Error('Effective date must be today or in the future.');
      }
      const targets = positions.filter(p => {
        if (!p.isActive) return false;
        if (args.departments === 'all') return true;
        return args.departments.includes(p.department);
      });
      await Promise.all(
        targets.map(p => {
          const raise = {
            id: newScheduledRaiseId(),
            effectiveDate: args.effectiveDate,
            kind: 'percent' as const,
            amount: args.percent,
            label: args.label,
            createdAt: now,
          };
          return updateDoc(doc(db, 'payrollPositions', p.id), {
            scheduledRaises: serializeScheduledRaises([...(p.scheduledRaises ?? []), raise]),
            updatedAt: Timestamp.fromDate(now),
            updatedBy: user.id,
          });
        }),
      );
    },
    [positions, user],
  );

  const deletePosition = useCallback(async () => {
    if (!editingPosition?.id) return;
    await deleteDoc(doc(db, 'payrollPositions', editingPosition.id));
  }, [editingPosition?.id]);

  const saveSettings = useCallback(async (next: Partial<PayrollSettings>) => {
    if (!user) return;
    const payload: any = {
      sepIraRate: next.sepIraRate ?? settings.sepIraRate,
      employerTaxRate: next.employerTaxRate ?? settings.employerTaxRate,
      defaultPtoDaysPerYear: next.defaultPtoDaysPerYear ?? settings.defaultPtoDaysPerYear,
      defaultHealthReimbursementMonthly: next.defaultHealthReimbursementMonthly ?? settings.defaultHealthReimbursementMonthly,
      defaultMonthlyEmployeeExtras: next.defaultMonthlyEmployeeExtras ?? settings.defaultMonthlyEmployeeExtras,
      defaultWorkState: next.defaultWorkState ?? settings.defaultWorkState ?? 'VA',
      updatedAt: Timestamp.fromDate(new Date()),
      updatedBy: user.id,
    };
    await setDoc(doc(db, 'financeSettings', PAYROLL_SETTINGS_DOC), payload, { merge: true });
  }, [user, settings]);

  const budgetedOpexMonthly = useMemo(() => totalBudgetedOpexMonthly(opexBudgets), [opexBudgets]);
  const opexBudgetMonthlyByGroup = useMemo(() => budgetedMonthlyByGroup(opexBudgets), [opexBudgets]);
  const monthlyBurn = useMemo(
    () => currentMonthlyBurn(overhead, positions, settings, new Date(), budgetedOpexMonthly),
    [overhead, positions, settings, budgetedOpexMonthly],
  );

  const monthlyPayrollByStatus = useMemo(
    () => payrollMonthlyByStatus(positions, settings, new Date()),
    [positions, settings],
  );

  const moveOverheadToGroup = useCallback(
    async (expenseId: string, group: PurchaseOverheadGroup | null) => {
      await updateDoc(doc(db, 'overheadExpenses', expenseId), {
        opexGroup: group || deleteField(),
        updatedAt: Timestamp.fromDate(new Date()),
      });
    },
    [],
  );

  const updateOpexBudgeted = useCallback(
    async (group: PurchaseOverheadGroup, amountUsd: number | null, interval: OpexBudgetInterval) => {
      if (!user) return;
      await saveOpexGroupBudget(group, { amountUsd, interval }, { userId: user.id });
    },
    [user],
  );

  const activeHeadcountNow = useMemo(
    () => activeHeadcountForMonth(positions, new Date()),
    [positions],
  );

  const hasLegacyPositions = useMemo(
    () => positions.some(p => p.benefitsAndTaxRate !== null && p.benefitsAndTaxRate !== undefined),
    [positions],
  );

  const counts = useMemo(() => ({
    overhead: overhead.length,
    payroll: positions.length,
    contractorPayables: pendingContractorInvoices,
    designerInvoiceReviews: isCpaShell || !canReviewDesignerInvoiceQueue ? 0 : pendingDesignerInvoiceCount,
  }), [canReviewDesignerInvoiceQueue, isCpaShell, overhead.length, pendingContractorInvoices, pendingDesignerInvoiceCount, positions.length]);

  const handleSelectSection = useCallback((s: FinancesSection) => {
    navigate(PATH_BY_SECTION[s]);
  }, [navigate]);

  const renderToolbar = () => {
    if (section === 'contractor-payables') return null;

    const stats = section === 'project-pl' ? (
      <div className="fin-hub-stats">
        <span className="fin-stat">Live revenue, cost, and budget variance by won project</span>
      </div>
    ) : section === 'regulatory' ? (
      <div className="fin-hub-stats">
        <span className="fin-stat">Surety bond request letters by project</span>
      </div>
    ) : showFullFinances && section !== 'progress-billing' ? (
      <div className="fin-hub-stats">
        <span className="fin-stat fin-stat-burn" title="Overhead + payroll this month">
          <span className="fin-stat-strong">{formatFinStatCurrency(monthlyBurn.total)}</span> / mo total burn
        </span>
        <span
          className="fin-stat fin-stat-payroll fin-loaded-tooltip-wrapper fin-payroll-stat-wrap"
          tabIndex={0}
          aria-label={`${formatFinStatCurrency(monthlyBurn.payroll)} per month loaded payroll`}
        >
          <span className="fin-stat-strong">{formatFinStatCurrency(monthlyBurn.payroll)}</span> / mo payroll
          <span className="fin-loaded-tooltip fin-payroll-status-tooltip" role="tooltip">
            <div className="fin-cost-breakdown-title">Loaded payroll this month</div>
            {PAYROLL_STATUSES.map((status) => (
              <div key={status} className="fin-payroll-status-row">
                <span>{PAYROLL_STATUS_LABELS[status]}</span>
                <span className="fin-payroll-status-amount">{formatFinStatCurrency(monthlyPayrollByStatus[status])}</span>
              </div>
            ))}
          </span>
        </span>
        <span className="fin-stat fin-stat-opex" title="Recurring overhead + budgeted envelopes (total burn minus payroll)">
          <span className="fin-stat-strong">{formatFinStatCurrency(monthlyBurn.overhead)}</span> / mo OpEx
        </span>
      </div>
    ) : null;

    let rightAction: React.ReactNode = null;
    if (section === 'progress-billing') {
      rightAction = (
        <ProgressBillingAsOfPicker value={progressAsOfInput} onChange={setProgressAsOfInput} />
      );
    } else if (section === 'regulatory' && canWriteFinances) {
      rightAction = (
        <button
          type="button"
          className="fin-primary-btn"
          onClick={() => setNewBondRequestNonce((n) => n + 1)}
        >
          <Plus size={14} /> New bond request
        </button>
      );
    } else if (section === 'overhead' && canWriteFinances) {
      rightAction = (
        <button type="button" className="fin-primary-btn" onClick={() => setEditingOverhead({})}>
          <Plus size={14} /> New expense
        </button>
      );
    } else if (section === 'payroll' && canWriteFinances) {
      rightAction = (
        <div className="fin-hub-toolbar-actions">
          <button
            type="button"
            className="fin-secondary-btn"
            onClick={() => setShowColaModal(true)}
          >
            Schedule COLA
          </button>
          <button
            type="button"
            className="fin-icon-btn"
            onClick={() => setEditingSettings(true)}
            aria-label="Payroll defaults"
            title="Payroll defaults"
          >
            <SettingsIcon size={14} />
          </button>
          <button type="button" className="fin-primary-btn" onClick={() => setEditingPosition({})}>
            <Plus size={14} /> New position
          </button>
        </div>
      );
    }

    return (
      <div className="fin-hub-toolbar">
        <div className="fin-hub-toolbar-left">
          <h2 className="fin-hub-title">
            {SECTION_TITLES[section]}
            {section === 'progress-billing' && <ProgressBillingMethodInfo />}
          </h2>
          {stats}
        </div>
        <div className="fin-hub-toolbar-right">{rightAction}</div>
      </div>
    );
  };

  if (!showFullFinances && !canPayables && !canAccessPL) {
    return <Navigate to="/" replace />;
  }

  if (!loaded) {
    return (
      <div className="fin-hub">
        <HubHeader />
        <div className="fin-hub-loading">
          <div className="fin-hub-loading-bar" />
        </div>
      </div>
    );
  }

  return (
    <div className="fin-hub">
      <HubHeader />
      {isPreview && (
        <div className="fin-cpa-preview-banner" role="status">
          <span>Previewing CPA portal — comments stay on, numbers are locked.</span>
          <button type="button" className="fin-secondary-btn" onClick={exitPreview}>
            Exit preview
          </button>
        </div>
      )}
      <div className="fin-hub-layout">
        <FinancesSidebar
          selected={section}
          onSelect={handleSelectSection}
          counts={counts}
          monthlyBurn={monthlyBurn.total}
          monthlyBurnBreakdown={{
            overhead: monthlyBurn.recurringOverhead,
            budgeted: monthlyBurn.budgeted,
          }}
          showFullFinances={showFullFinances}
          showProjectPL={canAccessPL}
          showPayables={canPayables}
          onPreviewCpa={user?.role === 'admin' && !isPreview ? enterPreview : undefined}
        />

        <div className="fin-hub-main">
          {renderToolbar()}

          <div className="fin-hub-content">
            {section === 'forecast' && (
              <ForecastView
                overhead={overhead}
                positions={positions}
                settings={settings}
                canWrite={canWriteFinances}
                canComment={canComment}
                budgetedOpexMonthly={budgetedOpexMonthly}
                opexBudgetMonthlyByGroup={opexBudgetMonthlyByGroup}
              />
            )}
            {section === 'overhead' && (
              <OverheadTable
                expenses={overhead}
                activeHeadcount={activeHeadcountNow}
                onEdit={canWriteFinances ? (e => setEditingOverhead(e)) : () => undefined}
                onAdd={canWriteFinances ? () => setEditingOverhead({}) : () => undefined}
                readOnly={!canWriteFinances}
                opexBudgets={opexBudgets}
                onBudgetedChange={canWriteFinances ? updateOpexBudgeted : undefined}
                onMoveToGroup={canWriteFinances ? moveOverheadToGroup : undefined}
              />
            )}
            {section === 'payroll' && (
              <PayrollTable
                positions={positions}
                crmUsers={crmUsers}
                settings={settings}
                hasLegacyPositions={hasLegacyPositions}
                onEdit={canWriteFinances ? (p => setEditingPosition(p)) : () => undefined}
                onAdd={canWriteFinances ? () => setEditingPosition({}) : () => undefined}
                onCreateJobPosting={canWriteFinances ? handleCreateJobPosting : undefined}
                readOnly={!canWriteFinances}
              />
            )}
            {section === 'project-pl' && (
              <ProjectPLSection />
            )}
            {section === 'progress-billing' && showFullFinances && (
              <ProgressBillingSection asOfInput={progressAsOfInput} />
            )}
            {section === 'contractor-payables' && canPayables && (
              <ContractorPayablesPage embedded readOnly={isCpaShell} />
            )}
            {section === 'regulatory' && showFullFinances && (
              <RegulatorySection
                readOnly={!canWriteFinances}
                newRequestNonce={newBondRequestNonce}
              />
            )}
          </div>
        </div>
      </div>

      {editingOverhead && (
        <OverheadEditModal
          initial={editingOverhead}
          activeHeadcount={activeHeadcountNow}
          onClose={() => setEditingOverhead(null)}
          onSave={upsertOverhead}
          onDelete={editingOverhead.id ? deleteOverhead : undefined}
        />
      )}

      {editingPosition && (
        <PayrollEditModal
          initial={editingPosition}
          crmUsers={crmUsers}
          settings={settings}
          onClose={() => setEditingPosition(null)}
          onSave={upsertPosition}
          onDelete={editingPosition.id ? deletePosition : undefined}
        />
      )}

      {editingSettings && (
        <PayrollSettingsModal
          initial={settings}
          onClose={() => setEditingSettings(false)}
          onSave={saveSettings}
        />
      )}

      {showColaModal && (
        <PayrollColaModal
          positions={positions}
          settings={settings}
          onClose={() => setShowColaModal(false)}
          onApply={applyCola}
        />
      )}
    </div>
  );
};

export default FinancesHub;
